import { type ItemContainerProps } from "@client/content/types";
import { globals } from "@client/globals";
import { joinDefined } from "@client/lib/misc";
import { addPrefix } from "@client/lib/object";
import { innerHorizontalAlignToJustifyContent } from "@client/lib/settings";
import {
  type SettingsPropsParametricSelector,
  TargetSelector,
  activeEventIdSelector,
  combinedSettingsSelector,
  combinedStyleSelector,
  createShallowEqualSelector,
  eventsItemsSelector,
  getColorSelector,
  getCornersRadiusSelector,
  getSettingValueSelector,
  makeTextStyleSelector,
  eventIdSelector as portalEventIdSelector
} from "@client/selectors";
import { type FormLayout } from "@client/types";
import * as React from "react";
import { createSelector } from "reselect";

export const eventIdSelector = createSelector(
  [activeEventIdSelector, portalEventIdSelector, eventsItemsSelector],
  (activeEventId, portalEventId, items): string | null | undefined => {
    if (globals.siteInfo.brandKey !== "Expomarket") return;

    const item = items.find((x) => x.id === (portalEventId ?? activeEventId));
    return item?.id ?? null;
  }
);

export const TextComponentPropsSelector = (state: any, props: ItemContainerProps) => props.TextComponent;

export const makeLabelStyleSelector = (target: string = TargetSelector.Entity, prefix?: string) =>
  makeTextStyleSelector(target, addPrefix("label", prefix));

export const makeFieldStyleSelector = (
  target: string = TargetSelector.Entity,
  prefix?: string
): SettingsPropsParametricSelector => {
  const path = joinDefined(".", prefix, "field");

  const backgroundColorSelector = getColorSelector(target, "backgroundColor", path);
  const radiusSelector = getCornersRadiusSelector(target, path);
  const styleSelector = combinedStyleSelector(target, ["border", "boxShadow", "margin", "text"], path);
  const paddingSettingsSelector = combinedSettingsSelector(target, ["size", "padding"], path);
  const paddingSelector = createShallowEqualSelector([paddingSettingsSelector], (settings): string | undefined => {
    switch (settings.size) {
      case "large":
        return "20px 30px";
      case "medium":
        return "10px 20px";
      case "small":
        return "5px 10px";
      default:
        return settings.padding;
    }
  });

  return createShallowEqualSelector(
    [backgroundColorSelector, styleSelector, paddingSelector, radiusSelector],
    (backgroundColor, style, padding, radius) => ({
      backgroundColor,
      ...style,
      padding: padding ?? "0px",
      borderRadius: radius ?? "0px",
      ["--text-color"]: style.color
    })
  );
};

export const makeFormButtonsStyleSelector = (target: string, prefix?: string): SettingsPropsParametricSelector => {
  const innerHorizontalAlignSelector = getSettingValueSelector(target, "innerHorizontalAlign", prefix);

  return createShallowEqualSelector(
    [innerHorizontalAlignSelector],
    (innerHorizontalAlign): React.CSSProperties => ({
      display: "flex",
      flexDirection: innerHorizontalAlign === "justify" ? "column" : "row",
      justifyContent: innerHorizontalAlignToJustifyContent(innerHorizontalAlign)
    })
  );
};

export const makeFormLayoutSelector = (target: string, prefix?: string): SettingsPropsParametricSelector => {
  const stackFieldsSelector = getSettingValueSelector(target, "stackFields", prefix);

  return createShallowEqualSelector(
    [stackFieldsSelector],
    (stackFields): FormLayout => ({
      stackFields: stackFields === true
    })
  );
};
